import { motion } from "framer-motion";
import { Users, Clock, Check, ExternalLink, Phone } from "lucide-react";

const points = [
  "Visite guidée du parc d'élevage et découverte du métier d'éleveur d'escargots",
  "Explications sur toutes les étapes : de la naissance à la commercialisation",
  "Dégustation de nos produits sur place, dans un cadre convivial",
  "Accueil des groupes et des particuliers, sur réservation",
];

const Visites = () => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section data-testid="visites-section" id="visites" className="relative py-24 lg:py-32 bg-creme overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-sm uppercase tracking-[0.3em] text-mousse font-500">L'expérience à la maison</span>
            <h2 className="font-heading text-4xl sm:text-5xl text-foreground mt-2 tracking-tight">
              Visites & dégustations
            </h2>
            <p className="text-lg text-foreground/75 mt-5 leading-relaxed">
              Le temps d'une visite, laissez-vous initier au monde délicat de l'héliciculture et
              découvrez le geste de l'éleveur. Chaque année, nous accueillons près de
              <strong className="text-bordeaux"> 600 hôtes</strong>, réunis autour d'une même
              curiosité gourmande et du plaisir d'une dégustation partagée.
            </p>

            <ul className="space-y-4 mt-8">
              {points.map((p, i) => (
                <li key={p} data-testid={`visite-point-${i}`} className="flex items-start gap-3 text-foreground/80">
                  <span className="mt-1 w-6 h-6 rounded-full bg-mousse/15 text-mousse flex items-center justify-center shrink-0">
                    <Check size={15} />
                  </span>
                  <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-6 mt-9">
              <div className="flex items-center gap-2 text-foreground/70"><Users size={18} className="text-bordeaux" /> Groupes & particuliers</div>
              <div className="flex items-center gap-2 text-foreground/70"><Clock size={18} className="text-bordeaux" /> Sur rendez-vous, de juin à septembre</div>
              <div className="flex items-center gap-2 text-foreground/70"><Clock size={18} className="text-bordeaux" /> Compter 2 heures</div>
              <div className="flex items-center gap-2 text-foreground/70"><Clock size={18} className="text-bordeaux" /> De 10h à midi · quelques créneaux à 18h</div>
            </div>

            <div data-testid="visites-tarif" className="mt-8 rounded-2xl bg-sable border border-border p-6 flex items-start gap-4">
              <span className="font-heading text-4xl text-bordeaux font-600 leading-none">9€</span>
              <p className="text-foreground/80 leading-relaxed">
                <strong className="text-foreground">Visite et dégustation</strong> comprenant
                6 Croq'Escargots et 2 tartines d'escargotines.
              </p>
            </div>

            <div data-testid="visites-vin-partenaire" className="mt-5 rounded-2xl bg-bordeaux text-white overflow-hidden">
              <div className="grid sm:grid-cols-2">
                <div className="bg-black/15 flex items-center justify-center p-4">
                  <div className="overflow-hidden rounded-lg">
                    <img
                      src="https://customer-assets.emergentagent.com/job_bourgogne-escargots/artifacts/90x6n09u_vin%20escargot.jpg"
                      alt="Vin du Domaine Mazille et escargots"
                      className="w-full h-auto object-contain block scale-[1.04]"
                    />
                  </div>
                </div>
                <div className="p-6 flex flex-col justify-center">
                  <div className="inline-flex self-start bg-white rounded-lg px-3 py-2">
                    <img
                      src="https://customer-assets.emergentagent.com/job_bourgogne-escargots/artifacts/bm0oxycn_image.png"
                      alt="Logo Domaine Mazille"
                      data-testid="mazille-logo"
                      className="h-12 w-auto object-contain"
                    />
                  </div>
                  <span className="text-xs uppercase tracking-[0.25em] text-white/70 mt-4">Le vin de nos dégustations</span>
                  <h3 className="font-heading text-2xl mt-1">Domaine Mazille</h3>
                  <p className="text-white/85 text-sm leading-relaxed mt-2">
                    Nous avons choisi <strong className="text-white">Baptiste Mazille</strong> parce qu'il
                    incarne profondément nos valeurs : le sens du partage, l'amour du terroir et un
                    savoir-faire transmis avec passion. Et parce que son vin est tout simplement
                    remarquable, il accompagne naturellement nos dégustations.
                  </p>
                  <a
                    href="https://www.domainemazille.com"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="mazille-link"
                    className="inline-flex items-center gap-2 mt-4 self-start rounded-full bg-white/15 hover:bg-white/25 text-white font-600 text-sm px-5 py-2.5 transition-colors"
                  >
                    Visiter le Domaine Mazille <ExternalLink size={15} />
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href="tel:0662125192"
                data-testid="visites-call-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-bordeaux text-white font-600 px-7 py-3.5 hover:bg-bordeaux-dark transition-all hover:-translate-y-0.5"
              >
                <Phone size={18} /> Appeler pour réserver
              </a>
              <button
                data-testid="visites-reserve-btn"
                onClick={() => go("contact")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-bordeaux text-bordeaux font-600 px-7 py-3.5 hover:bg-bordeaux hover:text-white transition-all"
              >
                Réserver par message
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_50px_rgba(44,42,40,0.2)] bg-black">
              <video
                data-testid="visites-video"
                src="https://customer-assets.emergentagent.com/job_bourgogne-escargots/artifacts/9cm00gb2_visites.mp4"
                poster="https://customer-assets.emergentagent.com/job_bourgogne-escargots/artifacts/yvgq8gr1_WhatsApp%20Image%202026-07-02%20at%2010.19.14%20%283%29.jpeg"
                controls
                autoPlay
                playsInline
                muted
                loop
                preload="auto"
                className="w-full h-[520px] object-cover"
              />
            </div>
            <div className="absolute -top-6 -right-6 bg-bordeaux text-white rounded-2xl px-7 py-5 shadow-xl hidden sm:block pointer-events-none">
              <div className="font-heading text-4xl font-600">600+</div>
              <div className="text-sm text-white/80">visiteurs chaque année</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Visites;
